// SNIGJ - Enhanced Documents Page with Editor
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import { documentsAPI } from '../lib/api';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Plus, Search, Filter, Eye, Download, FileText, FileImage, File, Upload, Calendar,
  Edit, Save, Bold, Italic, Underline, List, ListOrdered, AlignLeft, AlignCenter, AlignRight,
  Table, Image, Link2, Undo, Redo, Printer, FileSpreadsheet, FilePlus, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

// Document Templates
const DOCUMENT_TEMPLATES = [
  { id: 'judgment', name: { fr: 'Jugement', en: 'Judgment' }, icon: FileText },
  { id: 'summons', name: { fr: 'Convocation', en: 'Summons' }, icon: FileText },
  { id: 'report', name: { fr: 'Procès-verbal', en: 'Report' }, icon: FileText },
  { id: 'appeal', name: { fr: 'Acte d\'appel', en: 'Appeal' }, icon: FileText },
  { id: 'certificate', name: { fr: 'Certificat', en: 'Certificate' }, icon: FileText },
  { id: 'blank', name: { fr: 'Document vierge', en: 'Blank Document' }, icon: FilePlus }
];

const DocumentsPage = () => {
  const { language, user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditorModal, setShowEditorModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ document_type: '' });
  const [uploadData, setUploadData] = useState({
    title: '', document_type: 'COURT_DOCUMENT', classification: 'PUBLIC', case_id: '', file: null
  });
  const [editorData, setEditorData] = useState({
    title: '', document_type: 'COURT_DOCUMENT', classification: 'PUBLIC', case_id: '',
    content: '', template: 'blank'
  });

  useEffect(() => {
    loadDocuments();
  }, [filters]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const params = { ...filters, limit: 100 };
      Object.keys(params).forEach(key => !params[key] && delete params[key]);
      const response = await documentsAPI.list(params);
      setDocuments(response.data);
    } catch (error) {
      console.error('Failed to load documents:', error);
      toast.error(language === 'fr' ? 'Erreur de chargement' : 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadData({ ...uploadData, file, title: uploadData.title || file.name });
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadData.file) {
      toast.error(language === 'fr' ? 'Veuillez sélectionner un fichier' : 'Please select a file');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadData.file);
      formData.append('title', uploadData.title);
      formData.append('document_type', uploadData.document_type);
      formData.append('classification', uploadData.classification);
      if (uploadData.case_id) formData.append('case_id', uploadData.case_id);
      await documentsAPI.upload(formData);
      toast.success(language === 'fr' ? 'Document téléversé' : 'Document uploaded');
      setShowUploadModal(false);
      loadDocuments();
      setUploadData({ title: '', document_type: 'COURT_DOCUMENT', classification: 'PUBLIC', case_id: '', file: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur de téléversement' : 'Upload failed'));
    } finally {
      setUploading(false);
    }
  };

  const handleCreateDocument = async () => {
    if (!editorData.title || !editorData.content) {
      toast.error(language === 'fr' ? 'Titre et contenu requis' : 'Title and content required');
      return;
    }
    setSaving(true);
    try {
      // Create a blob from content and upload
      const blob = new Blob([editorData.content], { type: 'text/html' });
      const file = new File([blob], `${editorData.title}.html`, { type: 'text/html' });
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', editorData.title);
      formData.append('document_type', editorData.document_type);
      formData.append('classification', editorData.classification);
      formData.append('content', editorData.content);
      if (editorData.case_id) formData.append('case_id', editorData.case_id);
      await documentsAPI.upload(formData);
      toast.success(language === 'fr' ? 'Document créé' : 'Document created');
      setShowEditorModal(false);
      loadDocuments();
      setEditorData({ title: '', document_type: 'COURT_DOCUMENT', classification: 'PUBLIC', case_id: '', content: '', template: 'blank' });
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur de création' : 'Creation failed'));
    } finally {
      setSaving(false);
    }
  };

  const applyTemplate = (templateId) => {
    const templates = {
      judgment: `<div style="text-align: center; margin-bottom: 20px;">
        <h2>RÉPUBLIQUE DÉMOCRATIQUE DU CONGO</h2>
        <p>MINISTÈRE DE LA JUSTICE</p>
        <h3>TRIBUNAL DE GRANDE INSTANCE DE _______</h3>
        <hr/>
        <h2>JUGEMENT N° _____/${new Date().getFullYear()}</h2>
      </div>
      <p><strong>AFFAIRE:</strong> _____ c/ _____</p>
      <p><strong>DATE:</strong> ${new Date().toLocaleDateString('fr-FR')}</p>
      <hr/>
      <h3>AU NOM DU PEUPLE CONGOLAIS</h3>
      <p>Le Tribunal de Grande Instance de ______, siégeant en matière ______, a rendu le jugement suivant:</p>
      <h4>LES FAITS</h4>
      <p>[Description des faits...]</p>
      <h4>EN DROIT</h4>
      <p>[Analyse juridique...]</p>
      <h4>PAR CES MOTIFS</h4>
      <p>Le Tribunal,</p>
      <p>- [Décision 1]</p>
      <p>- [Décision 2]</p>
      <p style="margin-top: 30px;"><strong>Fait à ______, le ${new Date().toLocaleDateString('fr-FR')}</strong></p>
      <p>Le Président: _______________________</p>
      <p>Le Greffier: _______________________</p>`,
      summons: `<div style="text-align: center; margin-bottom: 20px;">
        <h2>RÉPUBLIQUE DÉMOCRATIQUE DU CONGO</h2>
        <p>MINISTÈRE DE LA JUSTICE</p>
        <h3>CONVOCATION</h3>
      </div>
      <p><strong>À l'attention de:</strong> M./Mme _____</p>
      <p><strong>Adresse:</strong> _____</p>
      <p><strong>Objet:</strong> Comparution devant le Tribunal</p>
      <hr/>
      <p>Vous êtes convoqué(e) à comparaître devant le _____ le <strong>_____ à _____</strong></p>
      <p><strong>Lieu:</strong> _____</p>
      <p><strong>Motif:</strong> _____</p>
      <p><strong>Dossier N°:</strong> _____</p>
      <p style="margin-top: 20px;">La présente convocation vaut mise en demeure. Votre absence non justifiée pourra entraîner des sanctions.</p>
      <p style="margin-top: 30px;">Fait à _____, le ${new Date().toLocaleDateString('fr-FR')}</p>
      <p>Signature: _______________________</p>`,
      report: `<div style="text-align: center; margin-bottom: 20px;">
        <h2>RÉPUBLIQUE DÉMOCRATIQUE DU CONGO</h2>
        <p>POLICE NATIONALE CONGOLAISE</p>
        <h3>PROCÈS-VERBAL</h3>
      </div>
      <p><strong>PV N°:</strong> _____/${new Date().getFullYear()}</p>
      <p><strong>Date:</strong> ${new Date().toLocaleDateString('fr-FR')}</p>
      <p><strong>Heure:</strong> _____</p>
      <p><strong>Lieu:</strong> _____</p>
      <hr/>
      <p>L'an deux mille _____, le _____, à _____ heures, Nous, _____, Officier de Police Judiciaire, en service à _____,</p>
      <p>Avons procédé aux constatations suivantes:</p>
      <h4>FAITS CONSTATÉS</h4>
      <p>[Description détaillée...]</p>
      <h4>PERSONNES IMPLIQUÉES</h4>
      <p>[Liste des personnes...]</p>
      <h4>MESURES PRISES</h4>
      <p>[Actions effectuées...]</p>
      <p style="margin-top: 30px;">Fait et clos le présent procès-verbal pour servir et valoir ce que de droit.</p>
      <p>L'Officier de Police Judiciaire: _______________________</p>`,
      blank: ''
    };
    setEditorData({ ...editorData, template: templateId, content: templates[templateId] || '' });
  };

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
  };

  const getFileIcon = (mimeType) => {
    if (mimeType?.startsWith('image/')) return FileImage;
    if (mimeType?.includes('pdf')) return FileText;
    if (mimeType?.includes('spreadsheet') || mimeType?.includes('excel')) return FileSpreadsheet;
    return File;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const documentTypes = ['COURT_DOCUMENT', 'POLICE_REPORT', 'EVIDENCE', 'WITNESS_STATEMENT', 'JUDGMENT', 'APPEAL', 'ADMINISTRATIVE', 'OTHER'];
  const classifications = ['PUBLIC', 'RESTRICTED', 'CONFIDENTIAL', 'SECRET', 'TOP_SECRET'];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="documents-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight">{t(language, 'documents.title')}</h1>
            <p className="text-muted-foreground mt-1">{language === 'fr' ? 'Système de gestion documentaire' : 'Document management system'}</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Create Document Button */}
            <Dialog open={showEditorModal} onOpenChange={setShowEditorModal}>
              <DialogTrigger asChild>
                <Button variant="outline" className="gap-2" data-testid="create-document-btn">
                  <FilePlus className="w-4 h-4" />
                  {language === 'fr' ? 'Créer un document' : 'Create Document'}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
                <DialogHeader>
                  <DialogTitle className="font-heading">{language === 'fr' ? 'Créer un document' : 'Create Document'}</DialogTitle>
                </DialogHeader>
                
                {/* Template Selection */}
                <div className="flex gap-2 overflow-x-auto py-2 border-b">
                  {DOCUMENT_TEMPLATES.map((template) => (
                    <Button key={template.id} variant={editorData.template === template.id ? 'default' : 'outline'} size="sm"
                      onClick={() => applyTemplate(template.id)} className="gap-2 shrink-0">
                      <template.icon className="w-4 h-4" />
                      {template.name[language]}
                    </Button>
                  ))}
                </div>

                <div className="grid grid-cols-4 gap-4 py-4">
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Titre' : 'Title'}</Label>
                    <Input value={editorData.title} onChange={(e) => setEditorData({ ...editorData, title: e.target.value })} required/>
                  </div>
                  <div className="space-y-2">
                    <Label>{t(language, 'documents.documentType')}</Label>
                    <Select value={editorData.document_type} onValueChange={(v) => setEditorData({ ...editorData, document_type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{documentTypes.map((type) => (<SelectItem key={type} value={type}>{type.replace(/_/g, ' ')}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t(language, 'cases.classification')}</Label>
                    <Select value={editorData.classification} onValueChange={(v) => setEditorData({ ...editorData, classification: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{classifications.map((c) => (<SelectItem key={c} value={c}>{t(language, `classifications.${c}`)}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'N° Dossier' : 'Case ID'}</Label>
                    <Input value={editorData.case_id} onChange={(e) => setEditorData({ ...editorData, case_id: e.target.value })} placeholder="case_xxxx"/>
                  </div>
                </div>

                {/* Editor Toolbar */}
                <div className="flex flex-wrap items-center gap-1 p-2 border rounded-t bg-muted/50">
                  <Button type="button" variant="ghost" size="icon" onClick={() => execCommand('bold')} title="Bold"><Bold className="w-4 h-4" /></Button>
                  <Button type="button" variant="ghost" size="icon" onClick={() => execCommand('italic')} title="Italic"><Italic className="w-4 h-4" /></Button>
                  <Button type="button" variant="ghost" size="icon" onClick={() => execCommand('underline')} title="Underline"><Underline className="w-4 h-4" /></Button>
                  <div className="w-px h-6 bg-border mx-1" />
                  <Button type="button" variant="ghost" size="icon" onClick={() => execCommand('justifyLeft')}><AlignLeft className="w-4 h-4" /></Button>
                  <Button type="button" variant="ghost" size="icon" onClick={() => execCommand('justifyCenter')}><AlignCenter className="w-4 h-4" /></Button>
                  <Button type="button" variant="ghost" size="icon" onClick={() => execCommand('justifyRight')}><AlignRight className="w-4 h-4" /></Button>
                  <div className="w-px h-6 bg-border mx-1" />
                  <Button type="button" variant="ghost" size="icon" onClick={() => execCommand('insertUnorderedList')}><List className="w-4 h-4" /></Button>
                  <Button type="button" variant="ghost" size="icon" onClick={() => execCommand('insertOrderedList')}><ListOrdered className="w-4 h-4" /></Button>
                  <div className="w-px h-6 bg-border mx-1" />
                  <Button type="button" variant="ghost" size="icon" onClick={() => execCommand('undo')}><Undo className="w-4 h-4" /></Button>
                  <Button type="button" variant="ghost" size="icon" onClick={() => execCommand('redo')}><Redo className="w-4 h-4" /></Button>
                </div>

                {/* Editor Content */}
                <div 
                  className="flex-1 min-h-[300px] max-h-[400px] overflow-y-auto p-4 border border-t-0 rounded-b bg-white dark:bg-slate-900"
                  contentEditable
                  dangerouslySetInnerHTML={{ __html: editorData.content }}
                  onInput={(e) => setEditorData({ ...editorData, content: e.currentTarget.innerHTML })}
                  style={{ outline: 'none' }}
                  data-testid="document-editor"
                />

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button variant="outline" onClick={() => setShowEditorModal(false)}>{t(language, 'common.cancel')}</Button>
                  <Button onClick={handleCreateDocument} disabled={saving} className="gap-2">
                    <Save className="w-4 h-4" />
                    {saving ? '...' : (language === 'fr' ? 'Enregistrer' : 'Save')}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Upload Button */}
            <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
              <DialogTrigger asChild>
                <Button className="gap-2" data-testid="upload-document-btn">
                  <Upload className="w-4 h-4" />
                  {t(language, 'documents.upload')}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-heading">{t(language, 'documents.upload')}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleUpload} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Fichier' : 'File'}</Label>
                    <div className="border-2 border-dashed rounded-sm p-6 text-center">
                      <input type="file" onChange={handleFileChange} className="hidden" id="file-upload" data-testid="file-input"/>
                      <label htmlFor="file-upload" className="cursor-pointer">
                        {uploadData.file ? (
                          <div className="flex items-center justify-center gap-3">
                            <FileText className="w-8 h-8 text-primary" />
                            <div className="text-left">
                              <p className="font-medium">{uploadData.file.name}</p>
                              <p className="text-sm text-muted-foreground">{formatFileSize(uploadData.file.size)}</p>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <Upload className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                            <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Cliquez pour sélectionner un fichier' : 'Click to select a file'}</p>
                          </div>
                        )}
                      </label>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Titre' : 'Title'}</Label>
                    <Input value={uploadData.title} onChange={(e) => setUploadData({ ...uploadData, title: e.target.value })} required data-testid="document-title"/>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t(language, 'documents.documentType')}</Label>
                      <Select value={uploadData.document_type} onValueChange={(value) => setUploadData({ ...uploadData, document_type: value })}>
                        <SelectTrigger data-testid="document-type-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{documentTypes.map((type) => (<SelectItem key={type} value={type}>{type.replace(/_/g, ' ')}</SelectItem>))}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>{t(language, 'cases.classification')}</Label>
                      <Select value={uploadData.classification} onValueChange={(value) => setUploadData({ ...uploadData, classification: value })}>
                        <SelectTrigger data-testid="document-classification"><SelectValue /></SelectTrigger>
                        <SelectContent>{classifications.map((c) => (<SelectItem key={c} value={c}>{t(language, `classifications.${c}`)}</SelectItem>))}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'N° Dossier (optionnel)' : 'Case ID (optional)'}</Label>
                    <Input value={uploadData.case_id} onChange={(e) => setUploadData({ ...uploadData, case_id: e.target.value })} placeholder="case_xxxx" data-testid="document-case-id"/>
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setShowUploadModal(false)}>{t(language, 'common.cancel')}</Button>
                    <Button type="submit" disabled={uploading} data-testid="upload-submit">{uploading ? t(language, 'common.loading') : t(language, 'documents.upload')}</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input placeholder={t(language, 'common.search') + '...'} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="documents-search"/>
                </div>
              </div>
              <div className="w-[180px]">
                <Select value={filters.document_type || "ALL"} onValueChange={(value) => setFilters({ ...filters, document_type: value === "ALL" ? "" : value })}>
                  <SelectTrigger data-testid="filter-document-type"><SelectValue placeholder={t(language, 'documents.documentType')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">{language === 'fr' ? 'Tous' : 'All'}</SelectItem>
                    {documentTypes.map((type) => (<SelectItem key={type} value={type}>{type.replace(/_/g, ' ')}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" onClick={loadDocuments} className="gap-2">
                <Filter className="w-4 h-4" />
                {t(language, 'common.filter')}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Documents Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {loading ? (
            Array.from({ length: 8 }).map((_, i) => (
              <Card key={i} className="overflow-hidden">
                <div className="aspect-[4/3] skeleton" />
                <CardContent className="p-4 space-y-2"><div className="h-5 skeleton w-3/4" /><div className="h-4 skeleton w-1/2" /></CardContent>
              </Card>
            ))
          ) : documents.length === 0 ? (
            <div className="col-span-full text-center py-12 text-muted-foreground">{t(language, 'common.noData')}</div>
          ) : (
            documents.map((doc) => {
              const FileIcon = getFileIcon(doc.mime_type);
              return (
                <Card key={doc.document_id} className="overflow-hidden hover-lift" data-testid={`document-card-${doc.document_id}`}>
                  <div className="aspect-[4/3] bg-muted flex items-center justify-center">
                    <FileIcon className="w-16 h-16 text-muted-foreground/50" />
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-medium truncate" title={doc.title}>{doc.title}</h3>
                    <div className="flex items-center justify-between mt-2 text-sm text-muted-foreground">
                      <span>{formatFileSize(doc.file_size)}</span>
                      <span className={`px-2 py-0.5 rounded text-xs classification-${doc.classification?.toLowerCase()}`}>
                        {t(language, `classifications.${doc.classification}`)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                      <Calendar className="w-3 h-3" />
                      {new Date(doc.uploaded_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US')}
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      <Button variant="outline" size="sm" className="flex-1 gap-1"><Eye className="w-3 h-3" />{t(language, 'documents.preview')}</Button>
                      <Button variant="outline" size="sm" className="flex-1 gap-1"><Download className="w-3 h-3" />{t(language, 'documents.download')}</Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DocumentsPage;
